/**
 * <s-expo-story>: Expo Mode on the homepage, a curved 3D road like Shopify Editions.
 * The corner toggle, Shift + ~ or #expo opens a full-screen layer. Each chapter [data-xs-ch] is a flat panel; the
 * panels are hinged edge to edge into one road whose tilt eases from lying down (where Mai is) to standing up
 * (further ahead), so the road bends up toward the horizon and the next moments face the viewer.
 * Scroll is smoothed (eased toward the scroll position each frame). Mai (the ball, face follows data-xs-mood) moves
 * along each panel's path through [data-xs-way] points; The4 (the orb) floats ahead. [data-xs-at] items get .is-hit
 * once Mai reaches them (data-xs-lead in px: positive = earlier). [data-xs-callout] items get a screen-space label
 * with a leader line. .xs-lift items stand up on hover (CSS, using each panel's --t). Space plays, 1–9 jump, Esc closes.
 */
const BALL_AT = 0.7;
const BALL_AT_SMALL = 0.58;
const ORB_AHEAD = 170;
const PLAY_SPEED = 300;
const STEP = 6;
const STRIP = 96;
const EASE = 7;

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const RAD = Math.PI / 180;
const hex = (h) => {
  const v = parseInt(h.replace('#', ''), 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
};
const mix = (a, b, f) => {
  const A = hex(a);
  const B = hex(b);
  return `rgb(${A.map((x, i) => Math.round(x + (B[i] - x) * f)).join(' ')})`;
};

// Expo Mode sound: everything is synthesized with Web Audio (no files). Night ambience whose filter opens toward
// sunrise, plus small cues. The choice (on/off) is remembered; audio only starts after the visitor opens the story.
const ROAD = ["#0d0a0b", "#120b0b", "#170c0c", "#1e0e0e", "#251010", "#301210", "#3a1410", "#4a1712", "#571a13", "#7c2419", "#b23a2c", "#d24c3e", "#e8584b", "#f2796c"];
const SCALE = [523.25, 587.33, 659.25, 783.99, 880, 1046.5, 1174.66];
class XsSound {
  constructor() {
    try {
      this.on = localStorage.getItem('the4-xs-sound') !== 'off';
    } catch {
      this.on = true;
    }
    this.ctx = null;
    this.master = null;
  }

  start() {
    if (!this.on) return;
    try {
      this.ctx ??= new (window.AudioContext || window.webkitAudioContext)();
    } catch {
      return;
    }
    const c = this.ctx;
    if (c.state === 'suspended') c.resume();
    if (!this.master) this.#build(c);
    this.master.gain.setTargetAtTime(0.45, c.currentTime, 0.6);
    if (!this.twinkleTimer) this.twinkleTimer = setTimeout(() => this.#twinkle(), 1500);
  }

  #build(c) {
    // Master → soft limiter, so overlapping cues never clip or crackle.
    const limiter = c.createDynamicsCompressor();
    limiter.threshold.value = -14;
    limiter.knee.value = 12;
    limiter.ratio.value = 6;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.25;
    limiter.connect(c.destination);
    this.master = c.createGain();
    this.master.gain.value = 0;
    this.master.connect(limiter);
    this.fx = c.createGain();
    this.fx.gain.value = 0.8;
    this.fx.connect(this.master);
    // Night air: very quiet, high and smooth (no low drones: they read as a hum on laptop speakers).
    this.noiseBuf = c.createBuffer(1, c.sampleRate * 4, c.sampleRate);
    const data = this.noiseBuf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i++) {
      last = last * 0.96 + (Math.random() * 2 - 1) * 0.04;
      data[i] = last * 6;
    }
    const air = c.createBufferSource();
    air.buffer = this.noiseBuf;
    air.loop = true;
    const hp = c.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 900;
    this.lp = c.createBiquadFilter();
    this.lp.type = 'lowpass';
    this.lp.frequency.value = 2400;
    const ag = c.createGain();
    ag.gain.value = 0.018;
    air.connect(hp).connect(this.lp).connect(ag).connect(this.master);
    air.start();
  }

  // Now and then a soft high note, like a star blinking.
  #twinkle() {
    clearTimeout(this.twinkleTimer);
    if (!this.on || !this.ctx) return;
    if (this.ctx.state === 'running') {
      const notes = [1046.5, 1174.66, 1318.5, 1567.98, 1760, 2093];
      const f = notes[Math.floor(Math.random() * notes.length)];
      this.#tone(f, { dur: 2.6, vol: 0.022 });
      this.#tone(f * 1.5, { at: 0.18, dur: 2, vol: 0.008 });
    }
    this.twinkleTimer = setTimeout(() => this.#twinkle(), 3500 + Math.random() * 4000);
  }

  stop() {
    clearTimeout(this.twinkleTimer);
    this.twinkleTimer = 0;
    if (this.master) this.master.gain.setTargetAtTime(0, this.ctx.currentTime, 0.15);
  }

  set(on) {
    this.on = on;
    try {
      localStorage.setItem('the4-xs-sound', on ? 'on' : 'off');
    } catch {}
    on ? this.start() : this.stop();
  }

  dawn(p) {
    if (this.lp) this.lp.frequency.setTargetAtTime(2400 + p * 2600, this.ctx.currentTime, 0.4);
  }

  #ready() {
    return this.on && this.ctx && this.master && this.ctx.state === 'running';
  }

  #tone(freq, { type = 'sine', at = 0, dur = 0.6, vol = 0.2, slide = 0 } = {}) {
    const c = this.ctx;
    const t = c.currentTime + at;
    const o = c.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (slide) o.frequency.exponentialRampToValueAtTime(freq * slide, t + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.fx);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  #noise(at, dur, vol, freq) {
    const c = this.ctx;
    const t = c.currentTime + at;
    const s = c.createBufferSource();
    s.buffer = this.noiseBuf;
    const f = c.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = freq;
    const g = c.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    s.connect(f).connect(g).connect(this.fx);
    s.start(t);
    s.stop(t + dur + 0.05);
  }

  play(name, n = 0) {
    if (!this.#ready()) return;
    const tone = (f, o) => this.#tone(f, o);
    if (name === 'chapter') {
      tone(SCALE[n % 7], { dur: 1.8, vol: 0.12 });
      tone(SCALE[n % 7] * 1.5, { at: 0.09, dur: 1.5, vol: 0.05 });
    } else if (name === 'pop') {
      tone(900, { dur: 0.12, vol: 0.1, slide: 0.6 });
    } else if (name === 'ting') {
      tone(330, { dur: 0.18, vol: 0.08, slide: 0.7 });
      tone(1318.5, { type: 'triangle', at: 0.06, dur: 1, vol: 0.13 });
      tone(1975.5, { at: 0.06, dur: 0.7, vol: 0.04 });
    } else if (name === 'bloom') {
      [659.25, 783.99, 987.77].forEach((f, i) => tone(f, { type: 'triangle', at: i * 0.07, dur: 0.9, vol: 0.06 }));
    } else if (name === 'open') {
      [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => tone(f, { at: i * 0.12, dur: 1.8, vol: 0.1 }));
    } else if (name === 'win') {
      [783.99, 987.77, 1174.66, 1567.98].forEach((f, i) => tone(f, { type: 'triangle', at: i * 0.08, dur: 1.3, vol: 0.08 }));
      this.#noise(0.6, 0.45, 0.1, 3200);
      for (let k = 0; k < 8; k++) tone(2000 + Math.random() * 1600, { at: 0.65 + k * 0.06, dur: 0.16, vol: 0.025 });
    } else if (name === "cash") {
      this.#noise(0, 0.06, 0.12, 5200);
      tone(1318.5, { type: "triangle", at: 0.03, dur: 0.18, vol: 0.12 });
      tone(1760, { type: "triangle", at: 0.12, dur: 0.9, vol: 0.12 });
      tone(2637, { at: 0.12, dur: 0.6, vol: 0.04 });
    } else if (name === 'lift') {
      tone(1320, { type: "triangle", dur: 0.16, vol: 0.03, slide: 1.2 });
    } else if (name === 'tick') {
      tone(1600, { dur: 0.05, vol: 0.05 });
    }
  }
}

class SExpoStory extends HTMLElement {
  #ctrl = null;
  #open = false;
  #raf = 0;
  #playing = false;
  #last = 0;
  #pos = 0;
  #chapters = [];
  #hits = [];
  #callouts = [];
  #strips = [];
  #ro = null;
  #lastFocus = null;
  #reduce = matchMedia('(prefers-reduced-motion: reduce)');
  #wide = matchMedia('(min-width: 1024px)');
  #curN = 0;
  #pv = "";
  #sun = "";
  #dawnAt = -1;
  #liftAt = 0;
  sound = new XsSound();

  connectedCallback() {
    this.#ctrl = new AbortController();
    const { signal } = this.#ctrl;
    const q = (s) => this.querySelector(s);
    this.toggle = q('[data-xs-toggle]');
    this.toggleLabel = q('[data-xs-toggle-label]');
    // Corner pill = Mai's little face (blinks now and then). Once the story has been opened and closed, it says
    // "Replay" with a tick (localStorage the4-xs-watched).
    if (localStorage.getItem('the4-xs-watched')) this.#markWatched();
    // Header Mai icon: let its "Mai's story" chip peek once per session on the homepage (unless already watched).
    const headerMai = document.querySelector('.hdr-expo');
    if (headerMai && !sessionStorage.getItem('the4-xs-peek') && !localStorage.getItem('the4-xs-watched')) {
      sessionStorage.setItem('the4-xs-peek', '1');
      setTimeout(() => headerMai.setAttribute('data-peek', ''), 2500);
      setTimeout(() => headerMai.removeAttribute('data-peek'), 6000);
    }
    this.layer = q('[data-xs-layer]');
    this.scroller = q('[data-xs-scroller]');
    this.world = q('[data-xs-world]');
    this.spacer = q('[data-xs-spacer]');
    this.ball = q('[data-xs-ball]');
    this.orb = q('[data-xs-orb]');
    this.void = this.querySelector(".xs-void");
    this.clockBar = this.querySelector(".xs-clock-bar span");
    this.storeFinal = this.querySelector(".xs-store-final");
    this.clock = q('[data-xs-clock]');
    this.coLayer = q('[data-xs-callouts]');
    this.coSvg = q('[data-xs-co-svg]');
    this.playBtn = q('[data-xs-play]');
    this.playLabel = q('[data-xs-play-label]');

    this.toggle.addEventListener('click', () => (this.#open ? this.close() : this.open()), { signal });
    q('[data-xs-close]').addEventListener('click', () => this.close(), { signal });
    this.playBtn.addEventListener('click', () => this.#setPlay(!this.#playing), { signal });
    this.soundBtn = q("[data-xs-sound]");
    this.soundBtn.setAttribute("aria-pressed", String(this.sound.on));
    this.soundBtn.addEventListener("click", () => {
      this.sound.set(!this.sound.on);
      this.soundBtn.setAttribute("aria-pressed", String(this.sound.on));
      this.sound.play("tick");
    }, { signal });
    // Browsers need a gesture before audio: also wake it on the first tap inside the layer (deep links).
    this.layer.addEventListener("pointerdown", () => this.#open && this.sound.start(), { signal });
    for (const lift of this.querySelectorAll(".xs-lift")) {
      // Lifted graphics lean a little toward the pointer, for a real 3D feel when they stand up.
      if (!lift.classList.contains("xs-copy")) {
        let raf = 0;
        lift.addEventListener("pointermove", (e) => {
          if (raf || e.pointerType !== "mouse" || !lift.classList.contains("is-up")) return;
          raf = requestAnimationFrame(() => {
            raf = 0;
            const r = lift.getBoundingClientRect();
            const x = clamp((e.clientX - r.left) / r.width) - 0.5;
            const y = clamp((e.clientY - r.top) / r.height) - 0.5;
            lift.style.setProperty("--ry", `${(x * 12).toFixed(2)}deg`);
            lift.style.setProperty("--rx", `${(-y * 8).toFixed(2)}deg`);
          });
        }, { signal });
      }
      // Hover intent: stand up after a short dwell, lie down after a short grace, and never while the road is moving,
      // so cards do not flicker at their edges or pop up as they slide under a still pointer.
      let enterT = 0;
      let leaveT = 0;
      let inside = false;
      const setUp = (on) => {
        if (lift.classList.contains("is-up") === on) return;
        lift.classList.toggle("is-up", on);
        if (on) {
          const now = performance.now();
          if (now - this.#liftAt > 180) this.sound.play("lift");
          this.#liftAt = now;
        } else {
          lift.style.removeProperty("--ry");
          lift.style.removeProperty("--rx");
        }
      };
      const tryUp = () => {
        if (!inside) return;
        if (this.#isMoving()) {
          enterT = setTimeout(tryUp, 120);
          return;
        }
        setUp(true);
      };
      lift.addEventListener("pointerenter", (e) => {
        if (e.pointerType === "touch") return;
        inside = true;
        clearTimeout(leaveT);
        clearTimeout(enterT);
        enterT = setTimeout(tryUp, lift.classList.contains("is-up") ? 0 : 70);
      }, { signal });
      lift.addEventListener("pointerleave", (e) => {
        if (e.pointerType === "touch") return;
        inside = false;
        clearTimeout(enterT);
        leaveT = setTimeout(() => setUp(false), 160);
      }, { signal });
      // Touch: tap toggles, one card up at a time.
      lift.addEventListener("pointerup", (e) => {
        if (e.pointerType !== "touch") return;
        const on = !lift.classList.contains("is-up");
        for (const other of this.querySelectorAll(".xs-lift.is-up")) if (other !== lift) other.classList.remove("is-up");
        setUp(on);
      }, { signal });
    }
    q('[data-xs-replay]')?.addEventListener('click', () => this.#replay(), { signal });
    for (const b of this.querySelectorAll('[data-xs-jump]')) {
      b.addEventListener('click', () => this.#jump(Number(b.dataset.xsJump)), { signal });
    }
    for (const chip of this.querySelectorAll('[data-xs-fit]')) {
      chip.addEventListener("click", () => {
        this.#pick(chip.dataset.xsFit);
        this.sound.play("bloom");
      }, { signal });
    }

    this.scroller.addEventListener('scroll', () => this.#kick(), { passive: true, signal });
    for (const type of ['wheel', 'touchstart']) {
      this.scroller.addEventListener(type, () => this.#setPlay(false), { passive: true, signal });
    }
    // Wheel over the HUD, index or callouts still walks the road.
    this.layer.addEventListener('wheel', (e) => {
      if (this.scroller.contains(e.target)) return;
      this.scroller.scrollTop += e.deltaY;
      this.#setPlay(false);
    }, { passive: true, signal });
    document.addEventListener('keydown', (e) => this.#onKey(e), { signal });
    window.addEventListener('resize', () => this.#open && this.#measure(), { signal });

    // Doors on the page (hero, launch passes, Trust, closing, Mai's greeting): any [data-expo-open="N"] opens the story
    // at chapter N (1 = start). Delegated so doors can live anywhere on the page.
    document.addEventListener('click', (e) => {
      const door = e.target.closest?.('[data-expo-open]');
      if (!door) return;
      e.preventDefault();
      const n = Number(door.dataset.expoOpen) || 1;
      this.open();
      if (n > 1) setTimeout(() => this.#jump(n, true), 120);
    }, { signal });

    // Shareable deep link: #expo opens the story, #expo-3 opens it at chapter 3.
    const deep = location.hash.match(/^#expo(?:-([1-7]))?$/);
    if (deep) {
      this.open();
      if (deep[1]) setTimeout(() => this.#jump(Number(deep[1]), true), 120);
    }
  }

  disconnectedCallback() {
    this.#ctrl?.abort();
    this.#ro?.disconnect();
    cancelAnimationFrame(this.#raf);
    document.documentElement.classList.remove('xs-open');
  }

  open() {
    if (this.#open) return;
    this.#open = true;
    sessionStorage.setItem('the4-xs-seen', '1');
    this.#lastFocus = document.activeElement;
    this.#stars();
    // Lazy images inside 3D transforms may never be seen as "near the viewport": load them all on open.
    for (const img of this.layer.querySelectorAll("img[loading=lazy]")) img.loading = "eager";
    this.sound.start();
    this.#curN = 0;
    this.layer.hidden = false;
    document.documentElement.classList.add('xs-open');
    this.toggle.setAttribute('aria-pressed', 'true');
    this.toggleLabel.textContent = this.dataset.strOn;
    this.scroller.scrollTop = 0;
    this.#pos = 0;
    requestAnimationFrame(() => {
      this.#measure();
      this.layer.classList.add('is-in');
      this.querySelector('[data-xs-close]').focus({ preventScroll: true });
    });
    this.#ro ??= new ResizeObserver(() => this.#open && this.#measure());
    for (const c of this.querySelectorAll('[data-xs-ch]')) this.#ro.observe(c);
  }

  #markWatched() {
    this.toggle.setAttribute('data-watched', '');
    this.toggleLabel.textContent = this.dataset.strReplay || this.dataset.strOff;
  }

  close() {
    if (!this.#open) return;
    this.#open = false;
    this.#setPlay(false);
    this.#ro?.disconnect();
    this.sound.stop();
    this.layer.classList.remove('is-in');
    this.layer.hidden = true;
    document.documentElement.classList.remove('xs-open');
    this.toggle.setAttribute('aria-pressed', 'false');
    localStorage.setItem('the4-xs-watched', '1');
    this.#markWatched();
    (this.#lastFocus && this.#lastFocus !== document.body ? this.#lastFocus : this.toggle).focus({ preventScroll: true });
  }

  #onKey(e) {
    const typing = e.target.closest?.('input, textarea, select, [contenteditable]');
    if (typing) return;
    if (e.key === '~' || (e.code === 'Backquote' && e.shiftKey)) {
      e.preventDefault();
      this.#open ? this.close() : this.open();
      return;
    }
    if (!this.#open) return;
    if (e.key === 'Escape') return this.close();
    if (e.key === ' ' && !e.target.closest('button, a')) {
      e.preventDefault();
      return this.#setPlay(!this.#playing);
    }
    if (/^[1-9]$/.test(e.key)) return this.#jump(Number(e.key));
    if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End'].includes(e.key)) this.#setPlay(false);
    if (e.key === 'Tab') this.#trap(e);
  }

  // Keep keyboard focus inside the open layer.
  #trap(e) {
    const items = [...this.layer.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (!this.layer.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    }
  }

  #stars() {
    const host = this.querySelector('[data-xs-stars]');
    if (!host || host.childElementCount) return;
    const frag = document.createDocumentFragment();
    for (let i = 0; i < 90; i++) {
      const s = document.createElement('span');
      const big = Math.random() < 0.12;
      s.style.cssText = `left:${(Math.random() * 100).toFixed(2)}%;top:${(Math.random() * 78).toFixed(2)}%;--s:${big ? 3 : 1 + Math.random() * 1.4}px;--d:${(2 + Math.random() * 4).toFixed(2)}s;--o:${(0.35 + Math.random() * 0.6).toFixed(2)}`;
      frag.append(s);
    }
    host.append(frag);
  }

  // Position inside a chapter panel, ignoring 3D transforms (offset chain).
  #local(el, panel) {
    let x = 0;
    let y = 0;
    let n = el;
    while (n && n !== panel) {
      x += n.offsetLeft;
      y += n.offsetTop;
      n = n.offsetParent;
    }
    return { x: x + el.offsetWidth / 2, y: y + el.offsetHeight / 2 };
  }

  #measure() {
    const wide = this.#wide.matches;
    this.t0 = wide ? 60 : 46;
    this.radius = wide ? 1500 : 1100;
    this.vh = this.scroller.clientHeight;
    this.vw = this.scroller.clientWidth;

    let start = 0;
    this.#chapters = [...this.querySelectorAll('[data-xs-ch]')].map((el) => {
      const W = el.offsetWidth;
      const H = el.offsetHeight;
      const lane = el.querySelector('[data-xs-lane]');
      const lx = lane ? lane.offsetLeft : W / 2;
      const svg = el.querySelector('.xs-path');
      const base = el.querySelector('[data-xs-path]');
      const done = el.querySelector('[data-xs-done]');
      const glow = el.querySelector("[data-xs-glow]");
      // Enter at the bottom of the lane, pass the waypoints, leave at the top of the lane: panels join seamlessly.
      const pts = [{ x: lx, y: H }, ...[...el.querySelectorAll('[data-xs-way]')].map((w) => this.#local(w, el)).sort((a, b) => b.y - a.y), { x: lx, y: 0 }];
      let d = `M${pts[0].x},${pts[0].y}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        d += ` C${p1.x + (p2.x - p0.x) / 6},${p1.y + (p2.y - p0.y) / 6} ${p2.x - (p3.x - p1.x) / 6},${p2.y - (p3.y - p1.y) / 6} ${p2.x},${p2.y}`;
      }
      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      svg.setAttribute('width', W);
      svg.setAttribute('height', H);
      base.setAttribute('d', d);
      done.setAttribute('d', d);
      glow?.setAttribute("d", d);
      const total = base.getTotalLength();
      done.style.strokeDasharray = `${total} ${total}`;
      if (glow) glow.style.strokeDasharray = `${total} ${total}`;
      const samples = [];
      let minY = Infinity;
      for (let len = 0; len <= total; len += STEP) {
        const p = base.getPointAtLength(len);
        minY = Math.min(minY, p.y);
        samples.push({ x: p.x, y: p.y, my: minY, len });
      }
      const ch = { el, n: Number(el.dataset.xsCh), W, H, start, total, samples, done, glow, mood: el.dataset.xsMood, time: el.querySelector('.xs-time')?.textContent || '', rail: this.querySelector(`[data-xs-rail="${el.dataset.xsCh}"]`) };
      start += H;
      return ch;
    });
    this.length = start;
    // Road strips, coloured along the night-to-sunrise gradient of the chapters they run under.
    const road = this.querySelector('[data-xs-road]');
    // Night to sunrise along the whole road: one colour stop per chapter edge (sunrise around "Open").
    const cols = this.#chapters.map((_, i) => [ROAD[Math.min(i, ROAD.length - 2)], ROAD[Math.min(i + 1, ROAD.length - 1)]]);
    const colorAt = (s) => {
      const c = this.#find(clamp(s, 0, this.length - 1)).ch;
      const i = this.#chapters.indexOf(c);
      return mix(cols[i][0], cols[i][1], clamp((s - c.start) / c.H));
    };
    this.stripH = STRIP + 2;
    const count = Math.ceil(this.length / STRIP);
    const frag = document.createDocumentFragment();
    this.#strips = [];
    for (let i = 0; i < count; i++) {
      const s = i * STRIP;
      const el = document.createElement('span');
      el.className = `xs-strip${i === 0 ? ' xs-strip-first' : ''}${i === count - 1 ? ' xs-strip-last' : ''}`;
      el.style.cssText = `height:${this.stripH}px;--c0:${colorAt(s)};--c1:${colorAt(s + STRIP)};--gy:${(s + STRIP) % 64}px`;
      frag.append(el);
      this.#strips.push({ el, s, far: false });
    }
    road.replaceChildren(frag);
    const first = this.#chapters[0];
    const last = this.#chapters[this.#chapters.length - 1];
    // Mai starts a little into the first panel and stops at its last waypoint before the edge of the road.
    this.s0 = first.H * 0.18;
    this.sEnd = last.start + last.H * (wide ? 0.26 : 0.5);
    this.travel = this.sEnd - this.s0;
    this.spacer.style.height = `${Math.round(this.travel)}px`;

    this.#hits = [...this.querySelectorAll('[data-xs-at]')].map((el) => {
      const ch = this.#chapters.find((c) => c.el.contains(el));
      const p = this.#local(el, ch.el);
      const cl = el.classList;
      const sfx = cl.contains("xs-say") || cl.contains("xs-msg") || cl.contains("xs-help") ? "pop" : cl.contains("xs-order") ? "cash" : cl.contains("xs-check") ? "tick" : cl.contains("xs-hole") ? "ting" : cl.contains("xs-open-wrap") ? "open" : el.querySelector("[data-xs-count]") ? "win" : cl.contains("xs-hole-row") ? "" : "bloom";
      return { el, sfx, s: ch.start + (ch.H - p.y) - Number(el.dataset.xsLead || 0), count: el.querySelector("[data-xs-count]") };
    });
    this.#setupCallouts();
    this.#pos = this.scroller.scrollTop;
    this.#render();
  }

  #setupCallouts() {
    this.coLayer.querySelectorAll('.xs-co').forEach((n) => n.remove());
    this.coSvg.replaceChildren();
    const ns = 'http://www.w3.org/2000/svg';
    this.#callouts = [...this.querySelectorAll('[data-xs-callout]')].map((el) => {
      const label = document.createElement('p');
      label.className = 'xs-co';
      label.textContent = el.dataset.xsCallout;
      this.coLayer.append(label);
      const line = document.createElementNS(ns, 'path');
      const dot = document.createElementNS(ns, 'circle');
      dot.setAttribute('r', '3.5');
      this.coSvg.append(line, dot);
      return { el, label, line, dot, ch: this.#chapters.find((c) => c.el.contains(el)) };
    });
  }

  // Smooth scroll: ease the drawn position toward the real scroll position every frame.
  #kick() {
    if (this.#raf) return;
    this.#last = performance.now();
    this.#raf = requestAnimationFrame((now) => this.#tick(now));
  }

  #isMoving() {
    return this.#playing || Math.abs(this.scroller.scrollTop - this.#pos) > 4;
  }

  #tick(now) {
    this.#raf = 0;
    if (!this.#open) return;
    const dt = Math.min(0.05, (now - this.#last) / 1000);
    this.#last = now;
    if (this.#playing) this.scroller.scrollTop += PLAY_SPEED * dt;
    const target = this.scroller.scrollTop;
    this.#pos = this.#reduce.matches ? target : this.#pos + (target - this.#pos) * (1 - Math.exp(-EASE * dt));
    if (Math.abs(target - this.#pos) < 0.3) this.#pos = target;
    this.#render();
    if (this.#playing || this.#pos !== target) this.#raf = requestAnimationFrame((t) => this.#tick(t));
  }

  #at(ch, y) {
    const s = ch.samples;
    let lo = 0;
    let hi = s.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (s[mid].my > y) lo = mid + 1;
      else hi = mid;
    }
    return s[lo];
  }

  #find(s) {
    const list = this.#chapters;
    let ch = list[0];
    for (const c of list) if (s >= c.start) ch = c;
    const p = this.#at(ch, ch.H - clamp(s - ch.start, 0, ch.H));
    return { ch, p };
  }

  #render() {
    if (!this.#open || !this.#chapters.length) return;
    const progress = clamp(this.#pos / this.travel, 0, 0.9995);
    const sb = this.s0 + progress * this.travel;
    const { ch: cur, p } = this.#find(sb);

    // One smooth road: a circular arc whose tilt eases from t0 (where Mai is) to upright ahead and a little flatter
    // behind, straight beyond the clamps. P(s) gives each point of the road relative to Mai's spot.
    const T0 = this.t0 * RAD;
    const TMIN = -4 * RAD;
    const TMAX = 78 * RAD;
    const R = this.radius;
    const sA = sb + (T0 - TMIN) * R;
    const sB = sb - (TMAX - T0) * R;
    const arc = (s) => {
      const t = T0 - (s - sb) / R;
      return { y: -R * (Math.sin(T0) - Math.sin(t)), z: -R * (Math.cos(t) - Math.cos(T0)) };
    };
    const P = (s) => {
      if (s > sA) {
        const a = arc(sA);
        return { y: a.y - (s - sA) * Math.cos(TMIN), z: a.z - (s - sA) * Math.sin(TMIN) };
      }
      if (s < sB) {
        const b = arc(sB);
        return { y: b.y - (s - sB) * Math.cos(TMAX), z: b.z - (s - sB) * Math.sin(TMAX) };
      }
      return arc(s);
    };
    // A flat piece from s0 to s1 placed as the chord of the road: bottom point + tilt.
    const place = (s0, s1) => {
      const b = P(s0);
      const e = P(s1);
      return { b, t: Math.atan2(-(e.z - b.z), -(e.y - b.y)) };
    };

    // Anything that has swung past the camera plane would be blown up across the screen: hide it.
    const gc = place(cur.start, cur.start + cur.H);
    const zCut = gc.b.z - (cur.H - p.y) * Math.sin(gc.t) + 700;
    for (const c of this.#chapters) {
      const g = place(c.start, c.start + c.H);
      const far = c !== cur && (c.start - sb > 5600 || sb - (c.start + c.H) > 1500 || P(c.start + c.H).z > zCut + 300);
      c.el.classList.toggle('is-far', far);
      c.b = g.b;
      c.t = g.t / RAD;
      if (far) continue;
      // Hover lifts read --t; it is inherited, so only write it when it moved half a degree.
      const tq = (Math.round(c.t * 5) / 5).toFixed(1);
      if (tq !== c.tq) {
        c.tq = tq;
        c.el.style.setProperty("--t", `${tq}deg`);
      }
      c.el.style.transform = `translate3d(0, ${(g.b.y - c.H).toFixed(1)}px, ${g.b.z.toFixed(1)}px) rotateX(${c.t.toFixed(2)}deg) translateZ(3px)`;
      const off = c.n < cur.n ? 0 : c.n > cur.n ? c.total : c.total - p.len;
      if (off !== c.off) {
        c.off = off;
        c.done.style.strokeDashoffset = `${off}`;
        if (c.glow) c.glow.style.strokeDashoffset = `${off}`;
      }
    }
    // The road surface: thin strips along the arc, so it reads as one continuous curve.
    const sh = this.stripH;
    for (const st of this.#strips) {
      const g = place(st.s, st.s + STRIP);
      const far = st.s - sb > 4600 || sb - st.s > 1100 || g.b.z > zCut;
      if (far !== st.far) {
        st.far = far;
        st.el.style.visibility = far ? 'hidden' : '';
      }
      if (far) continue;
      st.el.style.transform = `translate3d(0, ${(g.b.y - sh).toFixed(1)}px, ${g.b.z.toFixed(1)}px) rotateX(${(g.t / RAD).toFixed(2)}deg)`;
    }
    // Keep Mai at the same spot on screen.
    const ct = cur.t * RAD;
    const ballY = cur.b.y - (cur.H - p.y) * Math.cos(ct);
    const ballZ = cur.b.z - (cur.H - p.y) * Math.sin(ct);
    this.world.style.transform = `translate3d(0, ${(this.vh * (this.#wide.matches ? BALL_AT : BALL_AT_SMALL) - ballY).toFixed(1)}px, ${(-ballZ).toFixed(1)}px)`;

    // Mai and The4 live on the panel they are crossing.
    if (this.ball.parentElement !== cur.el) cur.el.append(this.ball);
    this.ball.style.transform = `translate3d(${p.x}px, ${p.y}px, 0)`;
    const o = this.#find(Math.min(sb + ORB_AHEAD, this.length - 1));
    if (this.orb.parentElement !== o.ch.el) o.ch.el.append(this.orb);
    this.orb.style.transform = `translate3d(${o.p.x}px, ${o.p.y}px, 0)`;

    // Only the sky (.xs-void), the clock bar and the final store use these: set them there, not on the whole layer,
    // so Safari does not restyle every element of the story each frame.
    const pv = progress.toFixed(3);
    if (pv !== this.#pv) {
      this.#pv = pv;
      this.void?.style.setProperty("--p", pv);
      this.clockBar?.style.setProperty("--p", pv);
    }
    const openCh = this.#chapters.find((c) => c.el.classList.contains('xs-ch-6'));
    const winCh = this.#chapters.find((c) => c.el.classList.contains('xs-ch-7'));
    const sun = clamp((sb - openCh.start) / (winCh.start + winCh.H * 0.4 - openCh.start)).toFixed(3);
    if (sun !== this.#sun) {
      this.#sun = sun;
      this.void?.style.setProperty("--sun", sun);
      this.storeFinal?.style.setProperty("--sun", sun);
    }

    const fresh = [];
    for (const hit of this.#hits) {
      const on = sb >= hit.s;
      const was = hit.el.classList.contains("is-hit");
      if (on && !was) fresh.push(hit);
      if (on && hit.count && !was) this.#count(hit.count);
      hit.el.classList.toggle('is-hit', on);
    }
    // One cue per frame, and none when a jump flips many at once.
    const cue = fresh.find((h) => h.sfx);
    if (cue && fresh.length <= 3) this.sound.play(cue.sfx);
    if (Math.abs(progress - this.#dawnAt) > 0.01) {
      this.#dawnAt = progress;
      this.sound.dawn(progress);
    }
    if (this.#curN !== cur.n) {
      if (this.#curN) this.sound.play("chapter", cur.n - 1);
      this.#curN = cur.n;
    }
    // Mood: worried until the doubts are answered.
    let mood = cur.mood;
    if (cur.el.querySelector('.xs-holes') && cur.el.querySelectorAll('.xs-hole.is-hit').length === 3) mood = 'glad';
    if (this.layer.dataset.mood !== mood) this.layer.dataset.mood = mood;
    if (this.clock.textContent !== cur.time) this.clock.textContent = cur.time;

    for (const c of this.#chapters) {
      const state = c === cur ? 'now' : c.n < cur.n ? 'done' : 'next';
      if (c.rail && c.rail.dataset.state !== state) {
        c.rail.dataset.state = state;
        c.rail.querySelector('button')?.toggleAttribute('aria-current', state === 'now');
      }
    }
    this.layer.classList.toggle('is-moved', progress > 0.01);
    const done = progress >= 0.995;
    this.layer.classList.toggle('is-done', done);
    if (done && this.#playing) this.#setPlay(false);
    this.#drawCallouts(cur);
  }

  // Leader-line labels in screen space, pointing at the projected position of each scene item.
  #drawCallouts(cur) {
    if (!this.#wide.matches) return;
    const vw = this.vw;
    const vh = this.vh;
    const used = { l: [], r: [] };
    // Read everything first (one layout), then write: no read-after-write layout thrash, which Safari punishes.
    const reads = this.#callouts.map((c) => {
      const near = c.ch.n >= cur.n - 1 && c.ch.n <= cur.n + 1;
      if (!near) return null;
      if (!c.lw) {
        c.lw = c.label.offsetWidth;
        c.lh = c.label.offsetHeight;
      }
      return c.el.getBoundingClientRect();
    });
    this.#callouts.forEach((c, i) => {
      const r = reads[i];
      const show = !!r && r.width > 40 && r.top > 70 && r.top < vh - 90 && r.bottom > 120;
      if (show !== c.on) {
        c.on = show;
        c.label.classList.toggle('is-on', show);
        c.line.classList.toggle('is-on', show);
        c.dot.classList.toggle('is-on', show);
      }
      if (!show) return;
      const right = r.left + r.width / 2 > vw / 2;
      const ax = right ? r.right - 6 : r.left + 6;
      const ay = r.top + Math.min(r.height * 0.3, 60);
      let ly = clamp(ay - 50, 80, vh - 120);
      for (const y of used[right ? 'r' : 'l']) if (Math.abs(y - ly) < 46) ly = y + 46;
      used[right ? 'r' : 'l'].push(ly);
      const lw = c.lw;
      const lx = right ? Math.min(vw - lw - 24, r.right + 56) : Math.max(250, r.left - 56 - lw);
      c.label.style.transform = `translate3d(${lx.toFixed(0)}px, ${ly.toFixed(0)}px, 0)`;
      const ex = right ? lx : lx + lw;
      const ey = ly + c.lh / 2;
      const kx = right ? ex - 18 : ex + 18;
      c.line.setAttribute('d', `M${ex.toFixed(0)},${ey.toFixed(0)} H${kx.toFixed(0)} L${ax.toFixed(0)},${ay.toFixed(0)}`);
      c.dot.setAttribute('cx', ax.toFixed(0));
      c.dot.setAttribute('cy', ay.toFixed(0));
    });
  }

  #count(el) {
    const end = Number(el.dataset.xsCount);
    if (this.#reduce.matches) return;
    const t0 = performance.now();
    const from = 0.6;
    const run = (now) => {
      const k = clamp((now - t0) / 1400);
      const e = 1 - Math.pow(1 - k, 3);
      el.textContent = (from + (end - from) * e).toFixed(1);
      if (k < 1) requestAnimationFrame(run);
    };
    requestAnimationFrame(run);
  }

  #setPlay(on) {
    if (on && this.scroller.scrollTop >= this.travel - 2) {
      this.scroller.scrollTop = 0;
      this.#pos = 0;
    }
    this.#playing = on;
    this.playBtn.setAttribute('aria-pressed', String(on));
    this.playLabel.textContent = on ? this.dataset.strPause : this.dataset.strPlay;
    if (on) this.#kick();
  }

  #jump(n, instant = false) {
    this.#setPlay(false);
    const c = this.#chapters.find((ch) => ch.n === n);
    if (!c) return;
    const target = clamp(c.start + c.H * 0.12 - this.s0, 0, this.travel);
    // The smoothing does the travel, so a plain jump still glides.
    this.scroller.scrollTop = target;
    if (instant || this.#reduce.matches) this.#pos = target;
    this.#kick();
  }

  #replay() {
    this.scroller.scrollTop = 0;
    this.#pos = 0;
    this.#setPlay(true);
  }

  #pick(niche) {
    this.dataset.fit = niche;
    for (const chip of this.querySelectorAll('[data-xs-fit]')) {
      chip.setAttribute('aria-pressed', String(chip.dataset.xsFit === niche));
    }
  }
}

if (!customElements.get('s-expo-story')) customElements.define('s-expo-story', SExpoStory);
